// ============================================================
// ROSTER SERIALIZATION - Combatant ⇄ its Firestore document.
//
// The document keeps the two halves in separate fields so they can be
// written independently (see hooks/useCombatantSync):
//
//   sheet / overrides  - JSON strings, matching the existing
//                        characters/{id}.data convention
//   state              - a NATIVE MAP, so a wound tick and a concurrent
//                        sheet edit merge server-side on disjoint fields
//
// Pure: no Firestore imports, so it is unit-testable on its own.
// ============================================================
import type { Character } from '../character/types';
import type { Combatant, CombatState, DefenderSheet, LoadoutRef } from './types';
import { normalizeCharacter } from '../character/normalize';
import { emptyCombatState } from './factories';
import { emptyCharacter } from '../character/factories';

export const COMBATANT_SCHEMA_VERSION = 2;

export interface CombatantDocShape {
  schemaVersion: number;
  name: string;
  kind: string;
  order: number;
  sheet: string;
  overrides: string;
  loadoutRef: LoadoutRef | null;
  /** Written dense. Read tolerantly - an older client still writes 40 cells. */
  slotOrder: string[] | null;
  /** The cold half's Lamport clock (v8.273.0); absent reads as 0. */
  sheetRev?: number;
  state: CombatState;
  updatedBy: string | null;
}

/**
 * Combatant → document.
 *
 * `narrativeLog` is deliberately NOT written: the shared log lives in the
 * narrative subcollection, and duplicating it here would double every
 * combat write for no benefit.
 */
export function combatantToDoc(c: Combatant, playerId: string | null): CombatantDocShape {
  return {
    schemaVersion: COMBATANT_SCHEMA_VERSION,
    name: c.name,
    kind: c.kind,
    order: c.order,
    sheet: JSON.stringify(c.sheet),
    overrides: JSON.stringify(c.overrides ?? {}),
    loadoutRef: c.loadoutRef ?? null,
    slotOrder: c.slotOrder ?? null,
    sheetRev: c.sheetRev ?? 0,
    state: c.state,
    updatedBy: playerId,
  };
}

/**
 * A stored arrangement, dense. Accepts the pre-8.189.0 sparse 40-cell shape
 * (ids and nulls) and anything malformed, and yields undefined for "no
 * arrangement" so the projection falls back to sheet order.
 */
export function sanitizeSlotOrder(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const ids = raw.filter((x): x is string => typeof x === 'string' && x.length > 0);
  return ids.length ? ids : undefined;
}

/**
 * The stored name and kind, defaulted.
 *
 * `||`, NOT `??`. A field nobody wrote comes back as `undefined` from a
 * schemaless store and as `''` from a typed column, and `??` falls
 * through only the first. So a combatant whose stored name was empty
 * arrived with no name at all - stats, tokens and all, but blank in the
 * roster and "No Target" in the panels. Three gotchas now say the same
 * thing: absent is not portable, so test the VALUE, not its nullness.
 */
function identityFromDoc(raw: Record<string, unknown>, sheet: Character): Pick<Combatant, 'name' | 'kind'> {
  return {
    name: (raw.name as string) || sheet.name || 'Combatant',
    kind: (raw.kind as Combatant['kind']) || 'statblock',
  };
}

/**
 * Document → Combatant. Tolerant by design: this is an ingress for data
 * written by other clients and possibly older versions, so a corrupt or
 * partial document degrades to a usable combatant rather than throwing.
 */
export function combatantFromDoc(id: string, raw: Record<string, unknown>): Combatant | null {
  try {
    const sheetJson = raw.sheet as string | undefined;
    const sheet: Character = sheetJson ? normalizeCharacter(JSON.parse(sheetJson)) : emptyCharacter(id);
    const overrides = raw.overrides ? (JSON.parse(raw.overrides as string) as Partial<DefenderSheet>) : {};
    const state = (raw.state as CombatState | undefined) ?? emptyCombatState(sheet);

    return {
      id,
      ...identityFromDoc(raw, sheet),
      sheet,
      overrides,
      state: { ...emptyCombatState(sheet), ...state },
      loadoutRef: (raw.loadoutRef as LoadoutRef | null) ?? null,
      // Sanitised on the way in, so nothing downstream has to know the old
      // sparse shape existed. An older client writes 40 cells of ids and
      // nulls; dropping the nulls IS the migration.
      slotOrder: sanitizeSlotOrder(raw.slotOrder),
      sheetRev: (raw.sheetRev as number) ?? 0,
      narrativeLog: [],
      order: (raw.order as number) ?? 0,
      createdAt: 0,
      updatedAt: 0,
    };
  } catch (err) {
    console.error('[roster] Failed to parse combatant doc:', id, err);
    return null;
  }
}

/**
 * Documents → roster, ordered, skipping anything unparseable.
 *
 * Takes `{ id, data }` PLAIN. It used to take Firestore's `{ id, data() }`,
 * which quietly made a backend's snapshot shape an argument type down here in
 * the engine. This is what the backend port delivers (`CollectionRead.entries`)
 * and what a test fixture can write without pretending to be Firestore.
 */
export function combatantsFromDocs(docs: RosterDoc[]): Combatant[] {
  return parsedCombatants(docs).map((p) => p.combatant);
}

/** One stored combatant document, already unwrapped. */
export interface RosterDoc {
  id: string;
  data: Record<string, unknown>;
}

export interface ParsedCombatant {
  combatant: Combatant;
  /**
   * Did the document actually CARRY a sheet?
   *
   * State is written on a 350ms debounce and the sheet on 700ms, so the
   * first snapshot after a combatant is created has state and no sheet.
   * `combatantFromDoc` fabricates an empty Character in that case so the
   * record is still usable - but that fabrication must never be accepted
   * over a real local sheet, or a freshly loaded character is renamed to
   * "New Character" and loses its wounds on the loader's own screen.
   */
  hasSheet: boolean;
  /**
   * The document's COLD fields exactly as stored.
   *
   * Echo prevention must compare these raw values, never a re-serialized
   * `combatant.sheet`: `combatantFromDoc` runs normalizeCharacter on the
   * way in, so a round-trip is not byte-identical and a re-serialized
   * comparison would report "changed" forever - the listener accepts,
   * the roster updates, the effect writes, and it never settles.
   * (Same rule useCharacterSync follows for the character library.)
   */
  rawCold: Record<string, unknown>;
}

export function parsedCombatants(docs: RosterDoc[]): ParsedCombatant[] {
  const out: ParsedCombatant[] = [];
  for (const d of docs) {
    const raw = d.data;
    const combatant = combatantFromDoc(d.id, raw);
    if (!combatant) continue;
    out.push({
      combatant,
      hasSheet: typeof raw.sheet === 'string' && raw.sheet.length > 0,
      rawCold: {
        name: raw.name ?? null,
        kind: raw.kind ?? null,
        order: raw.order ?? 0,
        sheet: raw.sheet ?? null,
        overrides: raw.overrides ?? null,
        loadoutRef: raw.loadoutRef ?? null,
        slotOrder: raw.slotOrder ?? null,
        schemaVersion: raw.schemaVersion ?? null,
      },
    });
  }
  return out.sort((a, b) => a.combatant.order - b.combatant.order);
}
